import { useState, useEffect } from 'react';
import { superAdminService } from '../../services/api';

interface Policy {
    maxAgeDays: number;
    warnDays: number;
    updatedAt?: string | null;
    updatedBy?: string | null;
}

interface Props {
    tenantId: string;
    email: string;
    onClose: () => void;
    onChanged?: () => void;
}

export default function TenantPolicyModal({ tenantId, email, onClose, onChanged }: Props) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [override, setOverride] = useState<Policy | null>(null);
    const [globalPolicy, setGlobalPolicy] = useState<Policy | null>(null);
    const [maxAgeInput, setMaxAgeInput] = useState<number>(0);
    const [warnInput, setWarnInput] = useState<number>(7);
    const [statusMsg, setStatusMsg] = useState<string | null>(null);

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const load = async () => {
        setLoading(true); setError(null);
        try {
            const res = await superAdminService.getTenantPasswordPolicy(tenantId);
            const data: any = res.data;
            if (data?.success) {
                setOverride(data.override);
                setGlobalPolicy(data.global);
                const seed = data.override ?? data.global;
                setMaxAgeInput(seed?.maxAgeDays ?? 0);
                setWarnInput(seed?.warnDays ?? 7);
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load tenant policy');
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        if (maxAgeInput < 0 || maxAgeInput > 3650) { setError('Max age must be 0–3650 days.'); return; }
        if (warnInput < 0 || warnInput > 365)     { setError('Warn days must be 0–365.'); return; }
        setSaving(true); setError(null); setStatusMsg(null);
        try {
            const res = await superAdminService.setTenantPasswordPolicy(tenantId, { maxAgeDays: maxAgeInput, warnDays: warnInput });
            const data: any = res.data;
            if (data?.success) {
                setOverride(data.override);
                setStatusMsg('Override saved. New policy takes effect within 60 seconds (cache TTL).');
                onChanged?.();
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const clear = async () => {
        if (!window.confirm(`Remove the password policy override for ${email}?\n\nThis tenant will fall back to the global default.`)) return;
        setSaving(true); setError(null); setStatusMsg(null);
        try {
            await superAdminService.clearTenantPasswordPolicy(tenantId);
            setOverride(null);
            setStatusMsg('Override removed. This tenant will use the global default within 60 seconds.');
            const g = globalPolicy;
            if (g) { setMaxAgeInput(g.maxAgeDays); setWarnInput(g.warnDays); }
            onChanged?.();
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Clear failed');
        } finally {
            setSaving(false);
        }
    };

    const enforced = (override?.maxAgeDays ?? globalPolicy?.maxAgeDays ?? 0) > 0;
    const source = override ? 'tenant override' : 'global default';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 rounded-t-2xl">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center">
                            <i className="fas fa-user-shield mr-3 text-amber-600"></i>
                            Password Policy Override
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">Tenant: <strong>{email}</strong></p>
                        <p className="text-xs text-gray-500 font-mono mt-0.5">{tenantId}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2" title="Close">×</button>
                </div>

                <div className="p-6 space-y-5">
                    {loading ? (
                        <div className="text-gray-500 py-8 text-center">Loading…</div>
                    ) : (
                        <>
                            <div className={`rounded-lg p-3 text-sm ${override ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-gray-50 border border-gray-200 text-gray-700'}`}>
                                <strong>Active source:</strong> {source}
                                {enforced
                                    ? <span> — {override?.maxAgeDays ?? globalPolicy?.maxAgeDays}-day max, warn at {override?.warnDays ?? globalPolicy?.warnDays}d.</span>
                                    : <span> — policy currently disabled.</span>}
                                {override?.updatedAt && (
                                    <div className="text-xs text-amber-700 mt-1">
                                        Override last changed: {new Date(override.updatedAt).toLocaleString('en-GB')}{override.updatedBy ? ` by ${override.updatedBy}` : ''}
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-800 mb-1">Max password age (days)</label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={3650}
                                        value={maxAgeInput}
                                        onChange={(e) => setMaxAgeInput(parseInt(e.target.value || '0', 10))}
                                        className="w-full border border-amber-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">0 disables enforcement for this tenant (overrides global).</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-800 mb-1">Warn N days before expiry</label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={365}
                                        value={warnInput}
                                        onChange={(e) => setWarnInput(parseInt(e.target.value || '0', 10))}
                                        className="w-full border border-amber-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                    />
                                </div>
                            </div>

                            {globalPolicy && (
                                <div className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded p-3">
                                    <i className="fas fa-info-circle text-blue-500 mr-1"></i>
                                    Global default is currently
                                    {globalPolicy.maxAgeDays > 0
                                        ? <> <strong>{globalPolicy.maxAgeDays}-day max</strong> (warn at {globalPolicy.warnDays}d).</>
                                        : <> <strong>disabled</strong>.</>}
                                    {' '}If no override is set, this tenant follows the global value.
                                </div>
                            )}

                            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
                            {statusMsg && !error && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">{statusMsg}</div>}
                        </>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex flex-wrap items-center justify-between gap-3">
                    <button
                        onClick={clear}
                        disabled={saving || !override}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={override ? 'Remove this override and fall back to global' : 'No override to clear'}
                    >
                        <i className="fas fa-eraser mr-2"></i>Clear override
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                        <button
                            onClick={save}
                            disabled={saving || loading}
                            className="px-5 py-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : (override ? 'Update override' : 'Set override')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
